import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { api } from '../lib/api'
import { useAuth } from './AuthContext'
import { Store } from '../types'
import { getCache, getCacheStale, setCache } from '../lib/cache'

interface StoreContextType {
  stores: Store[]
  activeStore: Store | null
  loading: boolean
  error: string | null
  isOnline: boolean
  setActiveStore: (store: Store | null) => void
  refreshStores: () => Promise<void>
  hasMultipleStores: boolean
}

const StoreContext = createContext<StoreContextType | undefined>(undefined)

interface StoreProviderProps {
  children: ReactNode
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  // Usar useAuth de forma segura
  let user = null
  let isStoreOwner = false
  try {
    const authContext = useAuth()
    user = authContext?.user || null
    isStoreOwner = authContext?.isStoreOwner || false
  } catch (error) {
    // Si AuthContext no está disponible, usar valores por defecto
    console.warn('AuthContext not available in StoreProvider:', error)
  }
  const [stores, setStores] = useState<Store[]>([])
  const [activeStore, setActiveStoreState] = useState<Store | null>(() => {
    // Intentar cargar activeStore desde localStorage al inicializar
    try {
      const saved = localStorage.getItem('activeStoreId')
      return saved ? null : null // Se establecerá después de cargar stores
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOnline, setIsOnline] = useState(true)

  // Ref para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingStoresRef = useRef(false)

  const hasMultipleStores = stores.length > 1

  // Función para establecer activeStore y guardarlo en localStorage
  const setActiveStore = (store: Store | null) => {
    setActiveStoreState(store)
    if (store) {
      localStorage.setItem('activeStoreId', store.id)
    } else {
      localStorage.removeItem('activeStoreId')
    }
  }

  const loadStores = async () => {
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingStoresRef.current) return
    loadingStoresRef.current = true

    // Solo cargar negocios si el usuario es dueño de negocio
    if (!user || !isStoreOwner) {
      setStores([])
      setActiveStore(null)
      setLoading(false)
      loadingStoresRef.current = false
      return
    }

    const cacheKey = `stores_${user.id}`
    
    // Intentar cargar desde cache primero
    const cachedStores = getCacheStale<Store[]>(cacheKey)
    if (cachedStores && cachedStores.length > 0) {
      setStores(cachedStores)
      setLoading(false)
      
      // Intentar restaurar activeStore desde localStorage
      try {
        const savedStoreId = localStorage.getItem('activeStoreId')
        if (savedStoreId) {
          const savedStore = cachedStores.find((s: Store) => s.id === savedStoreId) || null
          if (savedStore) {
            setActiveStore(savedStore)
          } else if (cachedStores.length > 0) {
            setActiveStore(cachedStores[0])
          }
        } else if (cachedStores.length > 0) {
          setActiveStore(cachedStores[0])
        }
      } catch {
        // Ignorar errores al restaurar
      }
      
      loadingStoresRef.current = false
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await api.getMyStores()
      const storeData = response

      // Marcar como online si la petición fue exitosa
      setIsOnline(true)

      // Cargar TODOS los stores (approved, pending, rejected, suspended)
      const allStores = storeData.all || []
      
      // Normalizar stores para asegurar que isActive y chargesItbis estén disponibles
      const normalizedStores = allStores.map((store: any) => ({
        ...store,
        isActive: store.is_active !== undefined ? store.is_active : store.isActive !== undefined ? store.isActive : false,
        chargesItbis: store.charges_itbis !== undefined ? store.charges_itbis : store.chargesItbis !== undefined ? store.chargesItbis : true
      }))

      setStores(normalizedStores)
      
      // Guardar en cache (1 hora de TTL)
      setCache(cacheKey, normalizedStores, 60 * 60 * 1000)

      // Filtrar solo negocios aprobados y activos para la lista de activeStores
      const activeStores = normalizedStores.filter((s: Store) => s.status === 'approved' && s.isActive === true)

      // Intentar restaurar activeStore desde localStorage buscando en TODOS los stores
      if (normalizedStores.length > 0) {
        try {
          const savedStoreId = localStorage.getItem('activeStoreId')
          if (savedStoreId) {
            // Buscar en TODOS los stores, no solo en los aprobados
            const savedStore = normalizedStores.find((s: Store) => s.id === savedStoreId)
            if (savedStore) {
              setActiveStore(savedStore)
            } else if (activeStores.length > 0) {
              // Si el store guardado no existe, usar el primero aprobado
              setActiveStore(activeStores[0])
            } else if (normalizedStores.length > 0) {
              // Si no hay aprobados, usar el primero disponible
              setActiveStore(normalizedStores[0])
            }
          } else {
            // Si no hay store guardado, preferir el primero aprobado, sino el primero disponible
            if (activeStores.length > 0) {
              setActiveStore(activeStores[0])
            } else if (normalizedStores.length > 0) {
              setActiveStore(normalizedStores[0])
            }
          }
        } catch {
          // Si hay error, usar el primero disponible
          if (normalizedStores.length > 0) {
            setActiveStore(normalizedStores[0])
          }
        }
      } else {
        setActiveStore(null)
      }

    } catch (err: any) {
      console.error('Error loading stores:', err)

      // Manejar diferentes tipos de errores de manera más específica
      if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || err.message?.includes('Network Error') || !err.response) {
        setIsOnline(false)
        setError('Sin conexión a internet. Mostrando datos en caché.')
        // Mantener stores existentes si hay error de red o datos en cache
        if (cachedStores && cachedStores.length > 0) {
          // Ya tenemos los datos del cache, no hacer nada más
          return
        }
        // Si no hay cache, mantener stores actuales si existen
        if (stores.length > 0) {
          return
        }
        // Si no hay stores ni cache, no hacer nada (mantener estado actual)
        return
      }

      if (err.response?.status === 500) {
        setIsOnline(false)
        setError('Servicio temporalmente no disponible. Intente más tarde.')
        // Mantener stores existentes si es error del servidor o hay cache
        if (cachedStores && cachedStores.length > 0) {
          return
        }
        if (stores.length > 0) {
          return
        }
        return
      }

      if (err.response?.status === 404) {
        setError('No se encontraron negocios registrados.')
        // Solo borrar si no hay cache
        if (!cachedStores || cachedStores.length === 0) {
          setStores([])
          setActiveStore(null)
        }
        return
      }

      // Error genérico - solo borrar si no hay cache
      setError(err.response?.data?.message || 'Error al cargar negocios')
      if (!cachedStores || cachedStores.length === 0) {
        setStores([])
        setActiveStore(null)
      }
    } finally {
      setLoading(false)
      loadingStoresRef.current = false
    }
  }

  const refreshStores = async () => {
    await loadStores()
  }

  // Cargar negocios cuando cambia el usuario o se monta el componente
  useEffect(() => {
    loadStores()
  }, [user, isStoreOwner])

  const value: StoreContextType = {
    stores,
    activeStore,
    loading,
    error,
    isOnline,
    setActiveStore,
    refreshStores,
    hasMultipleStores
  }

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  )
}

export const useStore = () => {
  const context = useContext(StoreContext)
  if (context === undefined) {
    // En lugar de lanzar error, retornar un objeto con valores por defecto
    // Esto permite que componentes funcionen incluso si el contexto no está disponible
    // (útil durante hot reload o inicialización)
    console.warn('useStore called outside StoreProvider, using default values')
    return {
      stores: [],
      activeStore: null,
      loading: false,
      error: null,
      isOnline: true,
      setActiveStore: () => {},
      refreshStores: async () => {},
      hasMultipleStores: false
    }
  }
  return context
}
